import { lazy, Suspense, type ReactElement, type ReactNode } from 'react';
import { createRootRoute, createRoute, createRouter, Outlet, useNavigate } from '@tanstack/react-router';
import { isId } from '@shared/ids';
import type { Id, IdPrefix } from '@shared/ids';
import { App } from './App';
import { AuthGate } from './auth/AuthGate';
import { AuthCallbackPage } from './auth/AuthCallbackPage';
import { ErrorBanner } from './components/Status';

const HomePage = lazy(() => import('./HomePage').then((module) => ({ default: module.HomePage })));
const ClientsPage = lazy(() =>
  import('./catalog/ClientsPage').then((module) => ({ default: module.ClientsPage })),
);
const ProductsPage = lazy(() =>
  import('./catalog/ProductsPage').then((module) => ({ default: module.ProductsPage })),
);
const ProductPage = lazy(() =>
  import('./catalog/ProductPage').then((module) => ({ default: module.ProductPage })),
);
const GalleryPage = lazy(() =>
  import('./catalog/GalleryPage').then((module) => ({ default: module.GalleryPage })),
);
const SceneComposer = lazy(() =>
  import('./composer/SceneComposer').then((module) => ({ default: module.SceneComposer })),
);
const GeneratePage = lazy(() =>
  import('./generate/GeneratePage').then((module) => ({ default: module.GeneratePage })),
);
const SettingsPage = lazy(() =>
  import('./settings/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);

function RouteSuspense({ children }: { readonly children: ReactNode }): ReactElement {
  return <Suspense fallback={<p className="p-6 text-sm text-neutral-500">Ladowanie…</p>}>{children}</Suspense>;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

/**
 * `/auth/callback` jest POZA `AuthGate` — w momencie klikniecia linku sesji
 * jeszcze nie ma, a `GET /api/me` zwrocilby 401 i pokazal ekran logowania nad
 * trwajaca wymiana tokenu.
 */
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  validateSearch: (search: Record<string, unknown>): { readonly token: string | null } => ({
    token: typeof search['token'] === 'string' ? search['token'] : null,
  }),
  component: AuthCallbackRouteComponent,
});

function AuthCallbackRouteComponent(): ReactElement {
  const { token } = authCallbackRoute.useSearch();
  const navigate = useNavigate();
  return (
    <AuthCallbackPage
      token={token}
      onDone={() => {
        void navigate({ to: '/' });
      }}
    />
  );
}

/** Warstwa bez wlasnej sciezki: sesja + powloka dla wszystkiego prywatnego. */
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: () => (
    <AuthGate>
      <App />
    </AuthGate>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: () => <RouteSuspense><HomePage /></RouteSuspense>,
});

const clientsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/clients',
  component: () => <RouteSuspense><ClientsPage /></RouteSuspense>,
});

const clientRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/clients/$clientId',
  component: ClientRouteComponent,
});

function ClientRouteComponent(): ReactElement {
  const { clientId } = clientRoute.useParams();
  const id = brandedId('cli', clientId);
  if (id === null) return <InvalidId raw={clientId} />;
  return <RouteSuspense><ProductsPage clientId={id} /></RouteSuspense>;
}

const productRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/products/$productId',
  component: ProductRouteComponent,
});

function ProductRouteComponent(): ReactElement {
  const { productId } = productRoute.useParams();
  const id = brandedId('prd', productId);
  if (id === null) return <InvalidId raw={productId} />;
  return <RouteSuspense><ProductPage productId={id} /></RouteSuspense>;
}

const productSceneRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/products/$productId/scene',
  component: ProductSceneRouteComponent,
});

function ProductSceneRouteComponent(): ReactElement {
  const { productId } = productSceneRoute.useParams();
  const id = brandedId('prd', productId);
  if (id === null) return <InvalidId raw={productId} />;
  return <RouteSuspense><SceneComposer productId={id} /></RouteSuspense>;
}

const generationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/generations/$generationId',
  component: GenerationRouteComponent,
});

function GenerationRouteComponent(): ReactElement {
  const { generationId } = generationRoute.useParams();
  const id = brandedId('gen', generationId);
  if (id === null) return <InvalidId raw={generationId} />;
  return <RouteSuspense><GeneratePage generationId={id} /></RouteSuspense>;
}

const galleryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gallery',
  component: () => <RouteSuspense><GalleryPage /></RouteSuspense>,
});

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: () => <RouteSuspense><SettingsPage /></RouteSuspense>,
});

/**
 * Parametr sciezki jest wejsciem NIEZAUFANYM. Brandowanie idzie przez `isId` z
 * `@shared/ids`, wiec zly identyfikator konczy sie komunikatem, a nie zapytaniem
 * do API o cos, co identyfikatorem nie jest.
 */
function brandedId<P extends IdPrefix>(prefix: P, raw: string): Id<P> | null {
  return isId(prefix, raw) ? raw : null;
}

function InvalidId({ raw }: { readonly raw: string }): ReactElement {
  return <ErrorBanner error={new Error(`Niepoprawny identyfikator w adresie: ${raw}`)} />;
}

const routeTree = rootRoute.addChildren([
  authCallbackRoute,
  appLayoutRoute.addChildren([
    indexRoute,
    clientsRoute,
    clientRoute,
    productRoute,
    productSceneRoute,
    generationRoute,
    galleryRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
